import { Component, effect, inject, OnInit } from '@angular/core';
import { DividerModule } from 'primeng/divider';
import { DropdownModule } from 'primeng/dropdown';
import { FormFieldWrapperComponent } from '../../molecules/form-field-wrapper/form-field-wrapper.component';
import { InputSwitchModule } from 'primeng/inputswitch';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { NgIf } from '@angular/common';
import { PaginatorModule } from 'primeng/paginator';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { SharedModule } from 'primeng/api';
import { BookmarkCategoryStore } from '../../../stores/bookmark-category/bookmark-category.state';
import { BookmarkCategory } from '../../../models/bookmark-category';
import { AppUtilityService } from '../../../utility/app.utility';
import { MultiSelectModule } from 'primeng/multiselect';
import { WorkspaceDropdownComponent } from '../../atoms/workspace-dropdown/workspace-dropdown.component';
import { BaseFormComponent } from '../../molecules/base-form/base-form.component';
import { ActivatedRoute, Router } from '@angular/router';
import { RoutingService } from '../../../services/routing.service';
import { ActiveUserStore } from '../../../stores/user/active-user.state';
import { UserDropdownComponent } from '../../atoms/user-dropdown/user-dropdown.component';
import { getState } from '@ngrx/signals';
import { PermissionService } from '../../../services/permission.service';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { FormFooterComponent } from '../../molecules/form-footer/form-footer.component';
import { PickListModule } from 'primeng/picklist';
import { HeaderComponent } from '../../atoms/header/header.component';
import { BookmarkStore } from '../../../stores/bookmark/bookmark.state';
import { Bookmark } from '../../../models/bookmark';
import { BookmarkCategoryHelperService } from '../../../services/bookmark-category/bookmark-category-helper.service';

import { PickListHeaderComponent } from '../../atoms/pick-list-header/pick-list-header.component';
@Component({
  selector: 'app-bookmark-category-form',
  standalone: true,
  imports: [
    DividerModule,
    DropdownModule,
    FormFieldWrapperComponent,
    InputSwitchModule,
    InputTextModule,
    InputTextareaModule,
    NgIf,
    PaginatorModule,
    ReactiveFormsModule,
    SharedModule,
    MultiSelectModule,
    WorkspaceDropdownComponent,
    UserDropdownComponent,
    FormFooterComponent,
    PickListModule,
    HeaderComponent,
    PickListHeaderComponent,
  ],
  templateUrl: './bookmark-category-form.component.html',
  styleUrl: './bookmark-category-form.component.scss',
})
export class BookmarkCategoryFormComponent
  extends BaseFormComponent<BookmarkCategory>
  implements OnInit
{
  readonly store = inject(BookmarkCategoryStore);
  readonly activeUserStore = inject(ActiveUserStore);
  readonly bookmarkStore = inject(BookmarkStore);
  readonly bookmarkCategoryHelper = inject(BookmarkCategoryHelperService);

  override form: FormGroup = this.fb.group({});
  override itemToEdit?: BookmarkCategory;
  override formInitialized = false;

  // Pick-list state: bookmarks not (yet) in this category vs. bookmarks
  // assigned to it. `bookmarks` on the category IS the source of truth now -
  // the pick-list just edits `form.get('bookmarks')` directly, exactly like
  // `topic-form.component.ts`'s `bookmarkCategories` pick-list.
  bookmarks: Bookmark[] = [];
  categoryBookmarks: Bookmark[] = [];
  /** bookmark id -> comma-joined titles of other categories it's already assigned to */
  bookmarkConflicts: Record<string, string> = {};
  // Tracks which category the pick-list was last built for, so it only gets
  // rebuilt when the edited category actually changes (e.g. the component
  // being reused for a different category via the router), not on every
  // unrelated re-fire of the store effect below - that would otherwise wipe
  // out the user's in-progress drag-and-drop selection.
  private picklistInitialized = false;
  private picklistBuiltForId: string | undefined;

  constructor(
    router: Router,
    route: ActivatedRoute,
    appUtilityService: AppUtilityService,
    routingService: RoutingService,
    permissionService: PermissionService,
    confirmationDialogService: ConfirmationDialogService,
    private fb: FormBuilder
  ) {
    super(
      router,
      route,
      appUtilityService,
      routingService,
      permissionService,
      confirmationDialogService
    );
    effect(() => {
      const state = getState(this.store);
      if (state.selectedId) {
        this.initializeForm();
      }
    });
  }

  override ngOnInit() {
    super.ngOnInit();
    if (!this.formInitialized) this.initializeForm();
  }

  /**
   * Initializes the form with predefined values from the item to edit, user store,
   * and other properties. Configures the reactive form structure with predefined
   * validators and default values.
   *
   * @return {void} Does not return a value. Sets up the form and initializes it within the component.
   */
  initializeForm(): void {
    this.itemToEdit = this.store.selectedItem();

    const targetId = this.itemToEdit?._id;
    const bookmarksReady = this.bookmarkStore.initialized();
    if (
      bookmarksReady &&
      (!this.picklistInitialized || this.picklistBuiltForId !== targetId)
    ) {
      this.picklistInitialized = true;
      this.picklistBuiltForId = targetId;

      const items = this.bookmarkStore.items();
      this.categoryBookmarks = (this.itemToEdit?.bookmarks || [])
        .map(id => items[id])
        .filter((bookmark): bookmark is Bookmark => !!bookmark);

      this.bookmarkConflicts = {};
      this.bookmarks = this.bookmarkStore
        .sorted()
        .filter(
          bookmark =>
            !bookmark.private &&
            !this.categoryBookmarks.some(
              assigned => assigned._id === bookmark._id
            )
        );
      this.bookmarks.forEach(bookmark =>
        this.setBookmarkConflictInfo(bookmark)
      );
    }

    this.form = this.fb.group({
      workspaces: [this.itemToEdit?.workspaces || ''],
      title: [this.itemToEdit?.title || '', Validators.required],
      private: [this.itemToEdit?.private || false],
      sorting: [this.itemToEdit?.sorting || this.store.count()],
      privateUsers: [
        this.itemToEdit?.privateUsers || [this.activeUserStore.user()?._id],
      ],
      bookmarks: [this.itemToEdit?.bookmarks || []],
    });

    this.formInitialized = true;
  }

  /**
   * Records, for a source-list (not-yet-assigned) bookmark, which OTHER
   * category(s) it's already assigned to - purely informational, doesn't
   * block assigning it here too.
   *
   * @param {Bookmark} bookmark - The bookmark to check.
   * @return {void} Does not return a value.
   */
  private setBookmarkConflictInfo(bookmark: Bookmark): void {
    const conflictingTitles = this.bookmarkCategoryHelper
      .getCategoriesForBookmark(bookmark._id)
      .map(categoryId => this.store.getById(categoryId)?.title)
      .filter((title): title is string => !!title);
    if (conflictingTitles.length > 0) {
      this.bookmarkConflicts[bookmark._id] = conflictingTitles.join(', ');
    }
  }

  /**
   * Updates the form's `bookmarks` field with the pick-list's current target
   * order - called on every pick-list move/reorder event, mirroring
   * `topic-form.component.ts`'s `updateFormValue()`. The array order IS the
   * display order within this category, so no separate sorting step is needed.
   *
   * @return {void} Does not return a value.
   */
  updateFormValue(): void {
    this.form.patchValue({
      bookmarks: this.categoryBookmarks.map(bookmark => bookmark._id),
    });
  }

  onSubmit(): void {
    this.submit();
  }
}
